import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@helios/ui/button';
import { Modal } from '@helios/ui/overlay';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qPatientFiles,
  qEncountersByPatient,
  type PatientFileRow,
  type PatientEncounterRow,
} from '@helios/data-access';
import { NavIcon, useBrandTokens, SidebarToggleButton } from '../../shell/shared';
import { usePatientShellContext } from '../../shell/PatientShellLayout';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { downloadFile } from '../../lib/download-file';
import { UploadDrawer, type ConsultationOption } from './consultation/UploadDrawer';
import {
  type PatientFileItem,
  PATIENT_FILE_FILTERS,
  PATIENT_FILE_SORTS,
  type PatientFileFilter,
  type PatientFileSort,
  formatFileSize,
  getFileExtension,
  isPreviewSupported,
  formatDateShort,
  formatDateTime,
} from './consultation/consultation-data';

// PRD.286 — Patient Files Library & Historical File Uploads.
//
// The patient-scoped master repository for every file. Reuses the existing
// patient_files table, the shared UploadDrawer, and the Firebase Storage
// pipeline. Uploads here collect a Record Date and auto-match a consultation
// (encounter) on that date; consultation-scoped uploads (Consultation → Files)
// keep working unchanged and surface here automatically because both write the
// same patient_files rows.

/** A file row enriched with derived display fields. */
interface FileView {
  id: string;
  filename: string;
  extension: string;
  kind: 'image' | 'document';
  url: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  uploadedBy: string | null;
  uploadDate: string | null; // created_at (ISO)
  recordDate: string | null; // 'YYYY-MM-DD'
  encounterId: string | null;
  consultationDate: string | null; // 'YYYY-MM-DD' from the linked encounter
  consultationLabel: string | null;
  previewable: boolean;
}

/** 'YYYY-MM-DD' for an encounter, from started_at (fallback created_at). */
function encounterDate(e: PatientEncounterRow): string | null {
  const iso = e.started_at ?? e.created_at;
  return iso ? iso.slice(0, 10) : null;
}

function encounterLabel(e: PatientEncounterRow): string {
  const date = encounterDate(e);
  const who = e.provider_name ? ` · ${e.provider_name}` : '';
  const type = e.type ? e.type.charAt(0).toUpperCase() + e.type.slice(1) : 'Consultation';
  return `${date ? formatDateShort(date) : 'Undated'} · ${type}${who}`;
}

export function PatientFilesPage({ patientId: patientIdProp }: { patientId?: string } = {}) {
  const params = useParams<{ id: string }>();
  const patientId = patientIdProp ?? params.id ?? '';
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#78716c';
  const navigate = useNavigate();
  const { collapsed: sidebarCollapsed, onToggleSidebar } = usePatientShellContext();
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const uploadedBy = user?.displayName ?? user?.email ?? null;

  const { data: fileRows = [] } = usePowerSyncQuery<PatientFileRow>(qPatientFiles(patientId));
  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncountersByPatient(patientId),
  );

  // encounter_id → { date, label } for consultation display + matching.
  const encounterMap = useMemo(() => {
    const m = new Map<string, { date: string | null; label: string }>();
    for (const e of encounterRows)
      m.set(e.id, { date: encounterDate(e), label: encounterLabel(e) });
    return m;
  }, [encounterRows]);

  // Consultation options handed to the UploadDrawer for Record-Date matching.
  const consultationOptions = useMemo<ConsultationOption[]>(
    () =>
      encounterRows
        .map((e) => ({ id: e.id, date: encounterDate(e) ?? '', label: encounterLabel(e) }))
        .filter((c) => c.date),
    [encounterRows],
  );

  const views = useMemo<FileView[]>(
    () =>
      fileRows.map((r) => {
        const filename = r.filename ?? '';
        const encId = r.encounter_id ?? null;
        const enc = encId ? encounterMap.get(encId) : undefined;
        return {
          id: r.id,
          filename,
          extension: getFileExtension(filename),
          kind: (r.kind ?? 'document') as 'image' | 'document',
          url: r.url ?? null,
          mimeType: r.mime_type ?? null,
          sizeBytes: r.size_bytes ?? null,
          uploadedBy: r.uploaded_by ?? null,
          uploadDate: r.created_at ?? null,
          recordDate: r.record_date ?? null,
          encounterId: encId,
          consultationDate: enc?.date ?? null,
          consultationLabel: enc?.label ?? null,
          previewable: isPreviewSupported({
            kind: r.kind,
            mimeType: r.mime_type,
            filename,
          }),
        };
      }),
    [fileRows, encounterMap],
  );

  const [filter, setFilter] = useState<PatientFileFilter>('All');
  const [sort, setSort] = useState<PatientFileSort>('Upload Date');
  const [search, setSearch] = useState('');
  // PRD.286 — list (default) / grid / continuous file views.
  const [viewMode, setViewMode] = useState<'list' | 'grid' | 'continuous'>('list');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileView | null>(null);
  const [editFile, setEditFile] = useState<FileView | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let result = views;
    switch (filter) {
      case 'Images':
        result = result.filter((f) => f.kind === 'image');
        break;
      case 'Documents':
        result = result.filter((f) => f.kind !== 'image');
        break;
      case 'Assigned':
        result = result.filter((f) => f.encounterId != null);
        break;
      case 'Unassigned':
        result = result.filter((f) => f.encounterId == null);
        break;
      default:
        break;
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (f) =>
          f.filename.toLowerCase().includes(q) ||
          (f.uploadedBy ?? '').toLowerCase().includes(q) ||
          (f.consultationLabel ?? '').toLowerCase().includes(q),
      );
    }

    // Sort (newest first for dates; A→Z for filename). Nulls sort last.
    const byDateDesc = (a: string | null, b: string | null) => {
      if (a === b) return 0;
      if (!a) return 1;
      if (!b) return -1;
      return b.localeCompare(a);
    };
    const sorted = [...result];
    sorted.sort((a, b) => {
      switch (sort) {
        case 'Consultation Date':
          return byDateDesc(a.consultationDate, b.consultationDate);
        case 'Record Date':
          return byDateDesc(a.recordDate, b.recordDate);
        case 'Filename':
          return a.filename.localeCompare(b.filename);
        case 'Upload Date':
        default:
          return byDateDesc(a.uploadDate, b.uploadDate);
      }
    });
    return sorted;
  }, [views, filter, search, sort]);

  const handleUpload = useCallback(
    async (file: PatientFileItem) => {
      if (!db) return;
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO patient_files
          (id, org_id, clinic_id, patient_id, encounter_id, visit_id, kind, filename, url, tag, mime_type, description, starred, record_date, size_bytes, uploaded_by, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          crypto.randomUUID(),
          orgId,
          null,
          patientId,
          file.encounterId ?? null,
          null,
          file.kind,
          file.filename,
          file.url ?? null,
          file.tag,
          file.mimeType ?? null,
          file.description ?? null,
          file.starred ? 1 : 0,
          file.recordDate ?? null,
          file.sizeBytes ?? null,
          file.uploadedBy ?? uploadedBy,
          now,
          now,
        ],
      );
    },
    [db, orgId, patientId, uploadedBy],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!db) return;
      await db.execute(`DELETE FROM patient_files WHERE id = ?`, [id]);
      setMenuOpenId(null);
    },
    [db],
  );

  const handleSaveEdit = useCallback(
    async (id: string, recordDate: string | null, encounterId: string | null) => {
      if (!db) return;
      await db.execute(
        `UPDATE patient_files SET record_date = ?, encounter_id = ?, updated_at = ? WHERE id = ?`,
        [recordDate, encounterId, new Date().toISOString(), id],
      );
      setEditFile(null);
    },
    [db],
  );

  // Shared action bundle for the grid / continuous card views.
  const fileActions: FileActions = {
    onPreview: (f) => f.previewable && f.url && setPreviewFile(f),
    onDownload: (f) => f.url && downloadFile(f.url, f.filename),
    onEdit: (f) => setEditFile(f),
    onDelete: handleDelete,
    menuOpenId,
    setMenuOpenId,
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        padding: 'var(--sp-3) var(--sp-8) var(--sp-6)',
      }}
    >
      {/* Header — breadcrumb: Patient Overview > Files */}
      <div style={{ marginBottom: 'var(--sp-4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 48 }}>
          <SidebarToggleButton collapsed={sidebarCollapsed} onToggle={onToggleSidebar} />
          <nav style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              onClick={() => navigate(`/patient/${patientId}/encounters`)}
              style={{
                background: 'none',
                border: 'none',
                color: primary,
                fontSize: 'var(--font-size-sm)',
                fontWeight: 500,
                cursor: 'pointer',
                padding: 0,
                whiteSpace: 'nowrap',
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              Patient Overview
            </button>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
              <NavIcon name="chevron-right" />
            </span>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--text)',
                whiteSpace: 'nowrap',
              }}
            >
              Files
            </span>
          </nav>
        </div>
        <p
          style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', margin: '2px 0 0' }}
        >
          Every file for this patient. Upload historical documents with a Record Date — they link to
          a consultation on that date automatically, or stay Unassigned.
        </p>
      </div>

      {/* Controls */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          flexWrap: 'wrap',
          marginBottom: 'var(--sp-4)',
        }}
      >
        {/* Search */}
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 300 }}>
          <div
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              display: 'flex',
              pointerEvents: 'none',
            }}
          >
            <NavIcon name="search" />
          </div>
          <input
            type="text"
            placeholder="Search files..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              height: 'var(--input-height)',
              padding: '0 var(--input-padding-x) 0 40px',
              borderRadius: 'var(--input-radius)',
              border: '1px solid var(--border-input)',
              background: 'var(--input-bg)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--input-text)',
              outline: 'none',
            }}
          />
        </div>

        {/* Filter chips */}
        {PATIENT_FILE_FILTERS.map((chip) => {
          const isActive = filter === chip;
          return (
            <button
              key={chip}
              type="button"
              onClick={() => setFilter(chip)}
              style={{
                height: 32,
                padding: '0 var(--sp-3)',
                borderRadius: 'var(--radius-full)',
                border: isActive ? 'none' : '1px solid var(--border-input)',
                background: isActive ? primary : 'var(--bg-surface)',
                color: isActive ? '#fff' : 'var(--text)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {chip}
            </button>
          );
        })}

        {/* Sort */}
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as PatientFileSort)}
          style={selectStyle}
        >
          {PATIENT_FILE_SORTS.map((s) => (
            <option key={s} value={s}>
              Sort: {s}
            </option>
          ))}
        </select>

        {/* View toggle: List (default) / Grid / Continuous */}
        <div
          style={{
            display: 'flex',
            border: '1px solid var(--border-input)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}
        >
          {(
            [
              { mode: 'list', title: 'List view' },
              { mode: 'grid', title: 'Grid view' },
              { mode: 'continuous', title: 'Continuous view' },
            ] as const
          ).map((v, i) => {
            const active = viewMode === v.mode;
            return (
              <button
                key={v.mode}
                type="button"
                onClick={() => setViewMode(v.mode)}
                title={v.title}
                aria-label={v.title}
                style={{
                  width: 36,
                  height: 34,
                  border: 'none',
                  borderLeft: i === 0 ? 'none' : '1px solid var(--border-input)',
                  background: active ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
                  color: active ? 'var(--text)' : 'var(--text-muted)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                }}
              >
                {v.mode === 'list' ? (
                  <NavIcon name="list" />
                ) : v.mode === 'grid' ? (
                  <NavIcon name="layout-grid" />
                ) : (
                  // Continuous / feed: two stacked cards.
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="1.5" y="2" width="13" height="5" rx="1.5" />
                    <rect x="1.5" y="9" width="13" height="5" rx="1.5" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>

        {/* Upload */}
        <Button variant="gradient" size="md" onClick={() => setUploadOpen(true)}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <NavIcon name="upload" />
            Upload
          </span>
        </Button>
      </div>

      {/* File list */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {filtered.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'var(--sp-12)',
              color: 'var(--text-muted)',
              gap: 'var(--sp-3)',
            }}
          >
            <NavIcon name="folder-open" />
            <span
              style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semi)' }}
            >
              No files found
            </span>
            <span style={{ fontSize: 'var(--font-size-sm)' }}>
              Upload the first file to build this patient&rsquo;s repository.
            </span>
            <Button variant="gradient" size="md" onClick={() => setUploadOpen(true)}>
              Upload
            </Button>
          </div>
        ) : viewMode === 'grid' ? (
          <GridView files={filtered} actions={fileActions} />
        ) : viewMode === 'continuous' ? (
          <ContinuousView files={filtered} actions={fileActions} />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[
                  'FILE',
                  'SIZE',
                  'UPLOADED BY',
                  'UPLOAD DATE',
                  'RECORD DATE',
                  'CONSULTATION',
                  '',
                ].map((col, i) => (
                  <th key={i} style={thStyle}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((f) => (
                <tr key={f.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  {/* File cell — thumbnail/icon + name + extension */}
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
                      <button
                        type="button"
                        onClick={() => f.previewable && f.url && setPreviewFile(f)}
                        title={f.previewable ? 'Preview' : 'No preview available'}
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 'var(--radius-md)',
                          background: '#f1f5f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--text-muted)',
                          flexShrink: 0,
                          overflow: 'hidden',
                          border: 'none',
                          padding: 0,
                          cursor: f.previewable && f.url ? 'pointer' : 'default',
                        }}
                      >
                        {f.kind === 'image' && f.url ? (
                          <img
                            src={f.url}
                            alt={f.filename}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <NavIcon name={f.kind === 'image' ? 'image' : 'file-text'} />
                        )}
                      </button>
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 'var(--font-weight-medium)',
                            color: 'var(--text)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 280,
                          }}
                          title={f.filename}
                        >
                          {f.filename}
                        </div>
                        <div
                          style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}
                        >
                          {f.extension || (f.kind === 'image' ? 'IMAGE' : 'FILE')}
                          {!f.previewable && ' · download only'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {formatFileSize(f.sizeBytes)}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {f.uploadedBy ?? '—'}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {f.uploadDate ? formatDateShort(f.uploadDate) : '—'}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {f.recordDate ? formatDateShort(f.recordDate) : '—'}
                  </td>
                  <td style={tdStyle}>
                    {f.encounterId ? (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '1px 8px',
                          borderRadius: 'var(--radius-full)',
                          background: '#e0e7ff',
                          color: '#3730a3',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 'var(--font-weight-medium)',
                          lineHeight: '20px',
                        }}
                        title={f.consultationLabel ?? undefined}
                      >
                        {f.consultationDate ? formatDateShort(f.consultationDate) : 'Linked'}
                      </span>
                    ) : (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '1px 8px',
                          borderRadius: 'var(--radius-full)',
                          background: '#f1f5f9',
                          color: '#64748b',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 'var(--font-weight-medium)',
                          lineHeight: '20px',
                        }}
                      >
                        Unassigned
                      </span>
                    )}
                  </td>
                  {/* Actions */}
                  <td style={{ ...tdStyle, position: 'relative', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => setMenuOpenId(menuOpenId === f.id ? null : f.id)}
                      style={{
                        width: 28,
                        height: 28,
                        border: 'none',
                        background: menuOpenId === f.id ? 'var(--bg-surface-2)' : 'none',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        color: 'var(--text-muted)',
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="5" r="1" />
                        <circle cx="12" cy="12" r="1" />
                        <circle cx="12" cy="19" r="1" />
                      </svg>
                    </button>
                    {menuOpenId === f.id && (
                      <RowMenu
                        onClose={() => setMenuOpenId(null)}
                        onPreview={f.previewable && f.url ? () => setPreviewFile(f) : undefined}
                        onDownload={f.url ? () => downloadFile(f.url!, f.filename) : undefined}
                        onEdit={() => {
                          setEditFile(f);
                          setMenuOpenId(null);
                        }}
                        onDelete={() => handleDelete(f.id)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <UploadDrawer
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUpload={handleUpload}
        orgId={orgId}
        patientId={patientId}
        brandTokens={brandTokens}
        recordDateMode
        consultations={consultationOptions}
        uploadedBy={uploadedBy}
      />

      {previewFile && previewFile.url && (
        <PreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
      )}

      {editFile && (
        <EditFileModal
          file={editFile}
          consultations={consultationOptions}
          onClose={() => setEditFile(null)}
          onSave={handleSaveEdit}
          portalStyle={brandTokens as CSSProperties}
        />
      )}
    </div>
  );
}

// ─── Row action menu ───

function RowMenu({
  onClose,
  onPreview,
  onDownload,
  onEdit,
  onDelete,
}: {
  onClose: () => void;
  onPreview?: () => void;
  onDownload?: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const items: { label: string; fn?: () => void; danger?: boolean }[] = [
    { label: 'Preview', fn: onPreview },
    { label: 'Download', fn: onDownload },
    { label: 'Edit details', fn: onEdit },
    { label: 'Delete', fn: onDelete, danger: true },
  ];
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 9 }} onClick={onClose} />
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: '100%',
          marginTop: 4,
          width: 160,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 10,
          overflow: 'hidden',
          textAlign: 'left',
        }}
      >
        {items.map((it) => (
          <button
            key={it.label}
            type="button"
            disabled={!it.fn}
            onClick={() => {
              it.fn?.();
              onClose();
            }}
            style={{
              width: '100%',
              padding: 'var(--sp-2) var(--sp-3)',
              background: 'none',
              border: 'none',
              textAlign: 'left',
              fontSize: 'var(--font-size-sm)',
              color: !it.fn
                ? 'var(--text-disabled, #cbd5e1)'
                : it.danger
                  ? '#dc2626'
                  : 'var(--text)',
              cursor: it.fn ? 'pointer' : 'not-allowed',
            }}
          >
            {it.label}
          </button>
        ))}
      </div>
    </>
  );
}

// ─── Shared card helpers (grid + continuous views) ───

interface FileActions {
  onPreview: (f: FileView) => void;
  onDownload: (f: FileView) => void;
  onEdit: (f: FileView) => void;
  onDelete: (id: string) => void;
  menuOpenId: string | null;
  setMenuOpenId: (id: string | null) => void;
}

function ConsultationBadge({ file }: { file: FileView }) {
  if (file.encounterId) {
    return (
      <span
        title={file.consultationLabel ?? undefined}
        style={{
          display: 'inline-block',
          padding: '1px 8px',
          borderRadius: 'var(--radius-full)',
          background: '#e0e7ff',
          color: '#3730a3',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-medium)',
          lineHeight: '20px',
        }}
      >
        {file.consultationDate ? formatDateShort(file.consultationDate) : 'Linked'}
      </span>
    );
  }
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 'var(--radius-full)',
        background: '#f1f5f9',
        color: '#64748b',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '20px',
      }}
    >
      Unassigned
    </span>
  );
}

/** ⋮ kebab button + dropdown, shared by the grid/continuous cards. */
function CardMenu({ file, actions }: { file: FileView; actions: FileActions }) {
  const open = actions.menuOpenId === file.id;
  return (
    <div style={{ position: 'relative', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => actions.setMenuOpenId(open ? null : file.id)}
        style={{
          width: 28,
          height: 28,
          border: 'none',
          background: open ? 'var(--bg-surface-2)' : 'rgba(255,255,255,0.85)',
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          color: 'var(--text-muted)',
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="5" r="1" />
          <circle cx="12" cy="12" r="1" />
          <circle cx="12" cy="19" r="1" />
        </svg>
      </button>
      {open && (
        <RowMenu
          onClose={() => actions.setMenuOpenId(null)}
          onPreview={file.previewable && file.url ? () => actions.onPreview(file) : undefined}
          onDownload={file.url ? () => actions.onDownload(file) : undefined}
          onEdit={() => actions.onEdit(file)}
          onDelete={() => actions.onDelete(file.id)}
        />
      )}
    </div>
  );
}

// ─── Grid view — responsive thumbnail cards ───

function GridView({ files, actions }: { files: FileView[]; actions: FileActions }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: 'var(--sp-4)',
      }}
    >
      {files.map((f) => (
        <div
          key={f.id}
          style={{
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border)',
            overflow: 'hidden',
            background: 'var(--bg-surface)',
          }}
        >
          <div
            onClick={() => f.previewable && f.url && actions.onPreview(f)}
            style={{
              position: 'relative',
              aspectRatio: '4 / 3',
              background: '#f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
              cursor: f.previewable && f.url ? 'zoom-in' : 'default',
            }}
          >
            {f.kind === 'image' && f.url ? (
              <img
                src={f.url}
                alt={f.filename}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <NavIcon name="file-text" />
            )}
            <div style={{ position: 'absolute', top: 8, right: 8 }}>
              <CardMenu file={f} actions={actions} />
            </div>
          </div>
          <div style={{ padding: 'var(--sp-3)', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={f.filename}
            >
              {f.filename}
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              {f.extension || (f.kind === 'image' ? 'IMAGE' : 'FILE')} ·{' '}
              {formatFileSize(f.sizeBytes)}
              {!f.previewable && ' · download only'}
            </div>
            <div>
              <ConsultationBadge file={f} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Continuous view — feed of large full-picture cards ───
//
// Each card shows the whole image (letterboxed, never cropped) with the
// filename + dates in a footer beneath it. Non-previewable files show a large
// icon placeholder so the feed stays uniform.

function ContinuousView({ files, actions }: { files: FileView[]; actions: FileActions }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-5)',
        alignItems: 'center',
      }}
    >
      {files.map((f) => (
        <div
          key={f.id}
          style={{
            width: '100%',
            maxWidth: 680,
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--bg-surface)',
            overflow: 'hidden',
          }}
        >
          {/* Full picture */}
          <div
            onClick={() => f.previewable && f.url && actions.onPreview(f)}
            style={{
              position: 'relative',
              background: '#0f172a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
              maxHeight: 600,
              cursor: f.previewable && f.url ? 'zoom-in' : 'default',
            }}
          >
            {f.kind === 'image' && f.url ? (
              <img
                src={f.url}
                alt={f.filename}
                style={{
                  maxWidth: '100%',
                  maxHeight: 600,
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 'var(--sp-2)',
                  color: '#cbd5e1',
                  padding: 'var(--sp-8)',
                }}
              >
                <NavIcon name="file-text" />
                <span style={{ fontSize: 'var(--font-size-sm)' }}>
                  {f.extension || 'FILE'}
                  {!f.previewable && ' · download only'}
                </span>
              </div>
            )}
            <div style={{ position: 'absolute', top: 8, right: 8 }}>
              <CardMenu file={f} actions={actions} />
            </div>
          </div>

          {/* Footer: name + dates */}
          <div
            style={{
              padding: 'var(--sp-3) var(--sp-4)',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--sp-3)',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={f.filename}
              >
                {f.filename}
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                  marginTop: 2,
                }}
              >
                {formatFileSize(f.sizeBytes)}
                {f.recordDate ? ` · Record ${formatDateShort(f.recordDate)}` : ''}
                {f.uploadDate ? ` · Uploaded ${formatDateShort(f.uploadDate)}` : ''}
              </div>
            </div>
            <ConsultationBadge file={f} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Preview modal (images inline, PDFs via iframe) ───

function PreviewModal({ file, onClose }: { file: FileView; onClose: () => void }) {
  const isPdf = file.mimeType === 'application/pdf' || file.extension === 'PDF';
  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 60 }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(90vw, 900px)',
          height: 'min(90vh, 720px)',
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-xl)',
          zIndex: 61,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--sp-3) var(--sp-4)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={file.filename}
          >
            {file.filename}
          </span>
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            {file.url && (
              <Button
                variant="outline"
                size="md"
                onClick={() => downloadFile(file.url!, file.filename)}
              >
                Download
              </Button>
            )}
            <Button variant="outline" size="md" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
        <div
          style={{
            flex: 1,
            background: '#0f172a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'auto',
          }}
        >
          {isPdf ? (
            <iframe
              src={file.url ?? ''}
              title={file.filename}
              style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
            />
          ) : (
            <img
              src={file.url ?? ''}
              alt={file.filename}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          )}
        </div>
      </div>
    </>
  );
}

// ─── Edit details modal (Record Date + Consultation) ───

function EditFileModal({
  file,
  consultations,
  onClose,
  onSave,
  portalStyle,
}: {
  file: FileView;
  consultations: ConsultationOption[];
  onClose: () => void;
  onSave: (id: string, recordDate: string | null, encounterId: string | null) => void;
  portalStyle: CSSProperties;
}) {
  const [recordDate, setRecordDate] = useState<string>(file.recordDate ?? '');
  const [encounterId, setEncounterId] = useState<string | null>(file.encounterId);

  // Consultations on the chosen Record Date, for the assignment dropdown.
  const matches = useMemo(
    () => (recordDate ? consultations.filter((c) => c.date === recordDate) : []),
    [consultations, recordDate],
  );

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Edit file details"
      maxWidth={460}
      portalStyle={portalStyle}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <Button variant="ghost" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            size="md"
            onClick={() => onSave(file.id, recordDate || null, encounterId)}
          >
            Save
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
        }}
      >
        <div>
          <label style={fieldLabelStyle}>Record Date</label>
          <input
            type="date"
            value={recordDate}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setRecordDate(e.target.value)}
            style={fieldInputStyle}
          />
        </div>
        <div>
          <label style={fieldLabelStyle}>Consultation</label>
          <select
            value={encounterId ?? ''}
            onChange={(e) => setEncounterId(e.target.value || null)}
            style={fieldInputStyle}
          >
            <option value="">Unassigned</option>
            {/* Keep the current link selectable even if its date differs. */}
            {file.encounterId &&
              !matches.some((m) => m.id === file.encounterId) &&
              file.consultationLabel && (
                <option value={file.encounterId}>{file.consultationLabel} (current)</option>
              )}
            {matches.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
          {recordDate && matches.length === 0 && (
            <div
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginTop: 4 }}
            >
              No consultation on {formatDateShort(recordDate)}.
            </div>
          )}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          Uploaded {file.uploadDate ? formatDateTime(file.uploadDate) : '—'}
          {file.uploadedBy ? ` by ${file.uploadedBy}` : ''}.
        </div>
      </div>
    </Modal>
  );
}

// ─── Styles ───

const thStyle: CSSProperties = {
  padding: 'var(--sp-3) var(--sp-4)',
  textAlign: 'left',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  borderBottom: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  whiteSpace: 'nowrap',
};

const tdStyle: CSSProperties = {
  padding: 'var(--sp-3) var(--sp-4)',
  fontSize: 'var(--font-size-sm)',
  verticalAlign: 'middle',
};

const fieldLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: 'var(--sp-2)',
};

const fieldInputStyle: CSSProperties = {
  width: '100%',
  height: 'var(--input-height)',
  padding: '0 var(--input-padding-x)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  outline: 'none',
};

const selectStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--sp-8) 0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  outline: 'none',
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
};
